import { ServicePluginDefinition } from '@wix/sdk-types';
import {
  Context,
  GetBalanceRequest,
  GetBalanceResponse,
  RedeemRequest,
  RedeemResponse,
  VoidRequest,
  VoidResponse,
} from './service-plugins-types.js';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';

export interface GetBalanceEnvelope {
  request: GetBalanceRequest;
  metadata: Context;
}

export interface RedeemEnvelope {
  request: RedeemRequest;
  metadata: Context;
}

export interface _voidEnvelope {
  request: VoidRequest;
  metadata: Context;
}

export const provideHandlers = ServicePluginDefinition<{
  /**
   *
   * This method retrieves gift card data from your app.
   * Wix calls this method when a customer applies a gift card as a payment method at checkout. */
  getBalance(
    payload: GetBalanceEnvelope
  ): GetBalanceResponse | Promise<GetBalanceResponse>;

  /**
   *
   * This method requests that a transaction be created by your app.
   * Wix calls this method when a customer completes a purchase that includes a gift card as a payment method at checkout. */
  redeem(payload: RedeemEnvelope): RedeemResponse | Promise<RedeemResponse>;

  /**
   *
   * This method requests that a gift card transaction be voided by your app.
   * Wix calls this method when a purchase fails after gift card redemption. */
  _void(payload: _voidEnvelope): VoidResponse | Promise<VoidResponse>;
}>('GIFT_CARDS_PROVIDER', [
  {
    name: 'getBalance',
    primaryHttpMappingPath: '/v1/balance',
    transformations: {
      toREST: (payload: any) => {
        const toRestResponse = transformPaths(payload, [
          {
            transformFn: transformSDKFloatToRESTFloat,
            paths: [{ path: 'balance' }],
          },
        ]);

        return renameKeysFromSDKRequestToRESTRequest(toRestResponse);
      },
      fromREST: (payload: any) => {
        const fromRestRequest = payload;

        return renameKeysFromRESTResponseToSDKResponse(fromRestRequest);
      },
    },
  },

  {
    name: 'redeem',
    primaryHttpMappingPath: '/v1/redeem',
    transformations: {
      toREST: (payload: any) => {
        const toRestResponse = transformPaths(payload, [
          {
            transformFn: transformSDKFloatToRESTFloat,
            paths: [{ path: 'remainingBalance' }],
          },
        ]);

        return renameKeysFromSDKRequestToRESTRequest(toRestResponse);
      },
      fromREST: (payload: any) => {
        const fromRestRequest = transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'request.amount' }],
          },
        ]);

        return renameKeysFromRESTResponseToSDKResponse(fromRestRequest);
      },
    },
  },

  {
    name: '_void',
    primaryHttpMappingPath: '/v1/void',
    transformations: {
      toREST: (payload: any) => {
        const toRestResponse = transformPaths(payload, [
          {
            transformFn: transformSDKFloatToRESTFloat,
            paths: [{ path: 'remainingBalance' }],
          },
        ]);

        return renameKeysFromSDKRequestToRESTRequest(toRestResponse);
      },
      fromREST: (payload: any) => {
        const fromRestRequest = payload;

        return renameKeysFromRESTResponseToSDKResponse(fromRestRequest);
      },
    },
  },
]);
